import React, { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Form, Input, Button, Typography, Flex, Alert, message } from 'antd'
import { EyeInvisibleOutlined, EyeTwoTone, ArrowLeftOutlined } from '@ant-design/icons'
import AppIcon from '../components/AppIcon'
import apiService from '../services/api'
import '../styles/global-input-fixes.css'

const { Title, Text } = Typography

const ResetPassword = () => {
  const [form] = Form.useForm()
  const [isLoading, setIsLoading] = useState(false)
  const [passwordReset, setPasswordReset] = useState(false)
  const [error, setError] = useState(null)
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  // Responsive styles
  const isMobile = window.innerWidth <= 768

  // Get token from URL parameters
  const token = searchParams.get('token')
  const email = searchParams.get('email')

  useEffect(() => {
    // Verificar se o token está presente e validá-lo
    if (!token) {
      setError('Token de recuperação inválido ou expirado.')
      return
    }

    // Validar o token com o backend
    const validateToken = async () => {
      try {
        const response = await apiService.auth.validateResetToken(token)
        if (!response.valid) {
          setError(response.message || 'Token de recuperação inválido ou expirado.')
        }
      } catch (error) {
        setError('Erro ao validar token. Tente novamente.')
      }
    }

    validateToken()
  }, [token])

  const handleSubmit = async (values) => {
    if (!token) {
      setError('Token de recuperação inválido ou expirado.')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      // Chamada real da API para redefinir senha
      await apiService.auth.resetPassword(token, values.password)

      setPasswordReset(true)
      message.success({
        content: 'Senha redefinida com sucesso!',
        duration: 5,
        style: {
          marginTop: '20vh',
        },
      })
    } catch (error) {
      const errorMessage = error.message || 'Erro ao redefinir senha. Tente novamente.'
      setError(errorMessage)
      message.error({
        content: errorMessage,
        duration: 5,
        style: {
          marginTop: '20vh',
        },
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleFieldChange = () => {
    if (error) {
      setError(null)
    }
  }

  if (passwordReset) {
    return (
      <div
        className="reset-password-container"
        style={{
          height: '100vh',
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          background: '#f5f5f5',
          overflow: 'hidden'
        }}
      >
        {/* Left Side - Image Section */}
        {isMobile ? (
          <div
            className="reset-password-left-section-mobile"
            style={{
              background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              height: '30vh',
              order: 1
            }}
          >
            <div
              className="reset-password-left-content"
              style={{
                textAlign: 'center',
                color: 'white',
                zIndex: 1,
                padding: 'clamp(20px, 5vw, 40px)'
              }}
            >
              <div style={{ marginBottom: '20px' }}>
                <AppIcon size={48} style={{ filter: 'brightness(0) invert(1)' }} />
              </div>
              <Title level={2} style={{ color: 'white', marginBottom: '8px', fontSize: 'clamp(24px, 5vw, 32px)' }}>Etiquetando</Title>
              <Text style={{ fontSize: 'clamp(14px, 2.5vw, 16px)', color: 'rgba(255, 255, 255, 0.9)' }}>
                Senha redefinida!
              </Text>
            </div>
          </div>
        ) : (
          <div
            className="reset-password-left-section"
            style={{
              flex: 1,
              background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              position: 'relative',
              overflow: 'hidden',
              height: '100vh',
              order: 1
            }}
          >
            <div
              className="reset-password-left-content"
              style={{
                textAlign: 'center',
                color: 'white',
                zIndex: 1,
                padding: 'clamp(20px, 5vw, 40px)'
              }}
            >
              <div style={{ marginBottom: '30px' }}>
                <AppIcon size={80} style={{ filter: 'brightness(0) invert(1)' }} />
              </div>
              <Title level={1} style={{ color: 'white', marginBottom: '16px', fontSize: 'clamp(32px, 6vw, 48px)' }}>Etiquetando</Title>
              <Text style={{ fontSize: 'clamp(16px, 3vw, 18px)', color: 'rgba(255, 255, 255, 0.9)' }}>
                Senha redefinida com sucesso!<br />
                Agora você pode fazer login.
              </Text>
            </div>
          </div>
        )}

        {/* Right Side - Success Message */}
        <div
          className="reset-password-right-section"
          style={{
            flex: isMobile ? 'none' : 1,
            width: isMobile ? '100%' : 'auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: isMobile ? '20px' : '40px',
            overflowY: 'auto',
            background: 'white',
            height: isMobile ? '70vh' : '100vh',
            order: 2
          }}
        >
          <div style={{ width: '100%', maxWidth: '400px', textAlign: 'center' }}>
            <div style={{ marginBottom: '24px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: '#52c41a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 20px',
                fontSize: '40px',
                color: 'white'
              }}>
                ✓
              </div>
              <Title level={2} style={{ color: '#1a1a1a', marginBottom: '8px' }}>Senha Redefinida!</Title>
              <Text style={{ fontSize: '16px', color: '#666' }}>
                Sua senha foi alterada com sucesso.
              </Text>
            </div>

            <Alert
              message="Pronto para usar"
              description="Agora você pode fazer login com sua nova senha."
              type="success"
              showIcon
              style={{ marginBottom: '24px', borderRadius: '8px', textAlign: 'left' }}
            />

            <Button
              type="primary"
              block
              onClick={() => navigate('/login')}
              style={{
                height: '45px',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '600',
                background: '#1890ff',
                border: 'none'
              }}
            >
              Fazer Login
            </Button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div
      className="reset-password-container"
      style={{
        height: '100vh',
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        background: '#f5f5f5',
        overflow: 'hidden'
      }}
    >
      {/* Left Side - Image Section */}
      {isMobile ? (
        <div
          className="reset-password-left-section-mobile"
          style={{
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            height: '30vh',
            order: 1
          }}
        >
          <div
            className="reset-password-left-content"
            style={{
              textAlign: 'center',
              color: 'white',
              zIndex: 1,
              padding: 'clamp(20px, 5vw, 40px)'
            }}
          >
            <div style={{ marginBottom: '20px' }}>
              <AppIcon size={48} style={{ filter: 'brightness(0) invert(1)' }} />
            </div>
            <Title level={2} style={{ color: 'white', marginBottom: '8px', fontSize: 'clamp(24px, 5vw, 32px)' }}>Etiquetando</Title>
            <Text style={{ fontSize: 'clamp(14px, 2.5vw, 16px)', color: 'rgba(255, 255, 255, 0.9)' }}>
              Redefinir Senha
            </Text>
          </div>
        </div>
      ) : (
        <div
          className="reset-password-left-section"
          style={{
            flex: 1,
            background: 'linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            overflow: 'hidden',
            height: '100vh',
            order: 1
          }}
        >
          <div
            className="reset-password-left-content"
            style={{
              textAlign: 'center',
              color: 'white',
              zIndex: 1,
              padding: 'clamp(20px, 5vw, 40px)'
            }}
          >
            <div style={{ marginBottom: '30px' }}>
              <AppIcon size={80} style={{ filter: 'brightness(0) invert(1)' }} />
            </div>
            <Title level={1} style={{ color: 'white', marginBottom: '16px', fontSize: 'clamp(32px, 6vw, 48px)' }}>Etiquetando</Title>
            <Text style={{ fontSize: 'clamp(16px, 3vw, 18px)', color: 'rgba(255, 255, 255, 0.9)' }}>
              Redefinir Senha<br />
              Digite sua nova senha.
            </Text>
          </div>
        </div>
      )}

      {/* Right Side - Reset Password Form */}
      <div
        className="reset-password-right-section"
        style={{
          flex: isMobile ? 'none' : 1,
          width: isMobile ? '100%' : 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: isMobile ? '20px' : '40px',
          overflowY: 'auto',
          background: 'white',
          height: isMobile ? '70vh' : '100vh',
          order: 2
        }}
      >
        <div style={{ width: '100%', maxWidth: '400px', padding: isMobile ? '5px 16px 16px 16px' : '0 16px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: isMobile ? 'flex-start' : 'center' }}>
          {/* Back Button */}
          <div style={{ marginBottom: isMobile ? '16px' : '20px' }}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate('/login')}
              style={{
                padding: '0px',
                color: '#1890ff',
                fontWeight: '500'
              }}
            >
              Voltar ao Login
            </Button>
          </div>

          {/* Welcome Text */}
          <div style={{ marginBottom: isMobile ? '16px' : '20px', textAlign: 'center' }}>
            <Title level={2} style={{ margin: '0px 0px 8px 0px', color: '#1a1a1a', fontWeight: '600' }}>Redefinir Senha</Title>
            <Text style={{ fontSize: '16px', color: '#666' }}>Digite sua nova senha</Text>
            {email && (
              <div style={{ marginTop: '8px' }}>
                <Text style={{ fontSize: '14px', color: '#999' }}>Para: {email}</Text>
              </div>
            )}
          </div>

          {/* Error Display */}
          {error && (
            <Alert
              message="Erro"
              description={error}
              type="error"
              showIcon
              style={{ marginBottom: '24px', borderRadius: '8px' }}
            />
          )}

          {/* Client Logo Area */}
          <div style={{ textAlign: 'center', marginBottom: '24px' }}>
            <AppIcon
              size={60}
              style={{
                objectFit: 'contain'
              }}
            />
          </div>

          {/* Reset Password Form */}
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            onFieldsChange={handleFieldChange}
            autoComplete="off"
          >
            {/* New Password Field */}
            <Form.Item
              label={<span style={{ color: '#333', fontWeight: '500' }}>Nova Senha</span>}
              name="password"
              rules={[
                {
                  required: true,
                  message: 'Por favor, insira sua nova senha!',
                },
                {
                  min: 6,
                  message: 'A senha deve ter pelo menos 6 caracteres!',
                },
              ]}
              style={{ marginBottom: isMobile ? '12px' : '16px' }}
            >
              <Input.Password
                placeholder="••••••••••"
                autoComplete="off"
                style={{
                  height: isMobile ? '40px' : '45px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  border: '1px solid #d9d9d9'
                }}
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              />
            </Form.Item>

            {/* Confirm Password Field */}
            <Form.Item
              label={<span style={{ color: '#333', fontWeight: '500' }}>Confirmar Nova Senha</span>}
              name="confirmPassword"
              dependencies={['password']}
              rules={[
                {
                  required: true,
                  message: 'Por favor, confirme sua nova senha!',
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve()
                    }
                    return Promise.reject(new Error('As senhas não coincidem!'))
                  },
                }),
              ]}
              style={{ marginBottom: isMobile ? '16px' : '20px' }}
            >
              <Input.Password
                placeholder="••••••••••"
                autoComplete="off"
                style={{
                  height: isMobile ? '40px' : '45px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  border: '1px solid #d9d9d9'
                }}
                iconRender={(visible) => (visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />)}
              />
            </Form.Item>

            {/* Submit Button */}
            <Form.Item style={{ marginBottom: isMobile ? '16px' : '20px' }}>
              <Button
                type="primary"
                htmlType="submit"
                block
                loading={isLoading}
                disabled={!token}
                style={{
                  height: isMobile ? '40px' : '45px',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  background: !token ? '#d9d9d9' : '#1890ff',
                  border: 'none'
                }}
              >
                Redefinir Senha
              </Button>
            </Form.Item>
          </Form>

          {/* Additional Info */}
          <div style={{ textAlign: 'center', marginTop: '16px' }}>
            <Text style={{ fontSize: '14px', color: '#666' }}>
              Lembrou da senha? {' '}
              <Link to="/login" style={{ color: '#1890ff', fontWeight: '500' }}>
                Fazer Login
              </Link>
            </Text>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ResetPassword